import type { DragEndEvent } from "@dnd-kit/core";
import { Add as AddIcon } from "@mui/icons-material";
import { Box, TextField, Typography } from "@mui/material";
import { type Control, Controller, useFieldArray, useFormContext, useFormState } from "react-hook-form";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import type { ContentType, LessonFormData } from "api/services/content/schemas/lessons";
import { MediaField } from "routes/library/components";
import { SortableList } from "shared/atoms/DragAndDrop";
import { MUIPositionedMenu } from "shared/atoms/MUIPositionedMenu";

import { SortableItem } from "./SortableItem";

interface Props {
  control: Control<LessonFormData>;
  isLoading?: boolean;
  partIndex: number;
  handleUploadFile: (event: React.ChangeEvent<HTMLInputElement>, partIndex: number, contentIndex: number) => void;
}

export const ContentSection: React.FC<Props> = ({ control, isLoading, partIndex, handleUploadFile }) => {
  const { t } = useTranslation();
  const { errors } = useFormState({ control });
  const { getValues } = useFormContext<LessonFormData>();
  const { fields, append, move, remove } = useFieldArray({
    control,
    name: `parts.${partIndex}.translation.content`,
  });

  const addContent = (type: ContentType) => {
    append({ type, content: "" });
  };

  const removeContent = (index: number) => {
    remove(index);
  };

  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = fields.findIndex(field => field.id === active.id);
      const newIndex = fields.findIndex(field => field.id === over.id);
      move(oldIndex, newIndex);
    }
  };

  const translateContentType = (type: ContentType) => {
    switch (type) {
      case "heading":
        return t("library.lessons.fields.content_options.heading");
      case "paragraph":
        return t("library.lessons.fields.content_options.paragraph");
      case "image":
        return t("library.lessons.fields.content_options.image");
      default:
        return t("library.lessons.fields.content_options.default");
    }
  };

  const options: { label: string; value: ContentType }[] = [
    { label: t("library.lessons.fields.content_options.heading"), value: "heading" },
    { label: t("library.lessons.fields.content_options.paragraph"), value: "paragraph" },
    { label: t("library.lessons.fields.content_options.image"), value: "image" },
  ];

  return (
    <Box sx={{ mt: "12px" }}>
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <Typography variant="h6">{t("library.lessons.fields.content")}</Typography>
        <MUIPositionedMenu
          buttonProps={{ children: t("buttons.add_content"), startIcon: <AddIcon /> }}
          options={options}
          handleMenuItemClick={option => addContent(option.value)}
        />
      </Box>

      <SortableList items={fields.map(f => f.id)} onDragEnd={onDragEnd}>
        {fields.map((fieldItem, index) => {
          const contentType = getValues(`parts.${partIndex}.translation.content.${index}.type`);

          return (
            <StyledSortableItem
              key={fieldItem.id}
              disableActions={isLoading}
              fieldItem={fieldItem}
              index={index}
              hasMoreThanOne={fields.length > 1}
              label={translateContentType(contentType)}
              variant="body1"
              wrapperComponent="accordion"
              onRemove={() => removeContent(index)}
            >
              <Controller
                name={`parts.${partIndex}.translation.content.${index}.content`}
                control={control}
                render={({ field }) => {
                  switch (contentType) {
                    case "heading":
                      return (
                        <TextField
                          {...field}
                          label={t("library.lessons.fields.content_options.heading")}
                          fullWidth
                          size="small"
                          error={!!errors.parts?.[partIndex]?.translation?.content?.[index]?.content}
                          helperText={
                            !!errors.parts?.[partIndex]?.translation?.content?.[index]?.content &&
                            t("errors.field.required")
                          }
                        />
                      );
                    case "paragraph":
                      return (
                        <TextField
                          {...field}
                          label={t("library.lessons.fields.content_options.paragraph")}
                          multiline
                          rows={4}
                          fullWidth
                          size="small"
                          error={!!errors.parts?.[partIndex]?.translation?.content?.[index]?.content}
                          helperText={
                            !!errors.parts?.[partIndex]?.translation?.content?.[index]?.content &&
                            t("errors.field.required")
                          }
                        />
                      );
                    case "image":
                      return (
                        <Box display="flex" flexDirection="column">
                          {field.value && (
                            <ImageContainer>
                              <img src={field.value} alt={`Content ${index + 1}`} />
                            </ImageContainer>
                          )}
                          <MediaField
                            accept="image/*"
                            isLoading={isLoading}
                            text={field.value ? t("buttons.change_image") : t("buttons.upload_image")}
                            handleUploadFile={event => handleUploadFile(event, partIndex, index)}
                            error={
                              errors.parts?.[partIndex]?.translation?.content?.[index]?.content
                                ? t("errors.field.required")
                                : undefined
                            }
                          />
                        </Box>
                      );
                    default:
                      return (
                        <TextField
                          {...field}
                          label={t("library.lessons.fields.content_options.default")}
                          fullWidth
                          size="small"
                        />
                      );
                  }
                }}
              />
            </StyledSortableItem>
          );
        })}
      </SortableList>
    </Box>
  );
};

const StyledSortableItem = styled(SortableItem)`
  & > .MuiAccordion-root {
    margin-bottom: 12px !important;
  }
`;

const ImageContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 8px;

  & > img {
    max-height: 100px;
    width: auto;
  }
`;
